import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { CloudArrowUpIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import { getPhotoUploadUrls, registerUploadedPhotos } from '../../lib/apiClient';

interface BulkPhotoUploaderProps {
  inspectionId: string;
  onUploadComplete?: () => void;
  onUpgradeRequired?: (message?: string) => void;
}

interface FilePreview {
  file: File;
  preview: string;
  id: string;
}

interface UploadProgress {
  total: number;
  completed: number;
  current: string;
}

export function BulkPhotoUploader({
  inspectionId,
  onUploadComplete,
  onUpgradeRequired,
}: BulkPhotoUploaderProps): JSX.Element {
  const [files, setFiles] = useState<FilePreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (files.length === 0) return;

      // Step 1: Get presigned upload URLs
      setUploadProgress({ total: files.length, completed: 0, current: 'Getting upload URLs...' });
      
      const urlsResponse = await getPhotoUploadUrls(inspectionId, {
        files: files.map((f) => ({
          filename: f.file.name,
          contentType: f.file.type || 'image/jpeg',
        })),
      });

      const uploads = urlsResponse.uploads;

      // Step 2: Upload each file directly to R2
      const uploadedPhotos: Array<{ key: string; filename: string; fileSize: number; contentType: string }> = [];

      for (let i = 0; i < files.length; i++) {
        const filePreview = files[i];
        const uploadInfo = uploads[i];

        setUploadProgress({
          total: files.length,
          completed: i,
          current: `Uploading ${filePreview.file.name}...`,
        });

        // Upload directly to R2 using presigned URL
        await fetch(uploadInfo.uploadUrl, {
          method: 'PUT',
          body: filePreview.file,
          headers: {
            'Content-Type': filePreview.file.type || 'image/jpeg',
          },
        });

        uploadedPhotos.push({
          key: uploadInfo.key,
          filename: filePreview.file.name,
          fileSize: filePreview.file.size,
          contentType: filePreview.file.type || 'image/jpeg',
        });
      }

      // Step 3: Register uploaded photos with backend
      setUploadProgress({
        total: files.length,
        completed: files.length,
        current: 'Registering photos & starting AI analysis...',
      });

      return registerUploadedPhotos(inspectionId, { photos: uploadedPhotos });
    },
    onSuccess: async () => {
      // Clear files after successful upload
      files.forEach((f) => URL.revokeObjectURL(f.preview));
      setFiles([]);
      setUploadProgress(null);
      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['inspection', inspectionId] });
      await queryClient.invalidateQueries({ queryKey: ['jobs', inspectionId] });
      onUploadComplete?.();
    },
    onError: (error) => {
      setUploadProgress(null);
      if (isAxiosError(error) && error.response?.status === 402) {
        onUpgradeRequired?.(error.response?.data?.message);
      }
    },
  });

  const handleFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return;
    const imageFiles = Array.from(newFiles).filter((file) =>
      file.type.startsWith('image/')
    );
    const previews: FilePreview[] = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: `${file.name}-${Date.now()}-${Math.random()}`,
    }));
    setFiles((prev) => [...prev, ...previews]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file) URL.revokeObjectURL(file.preview);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    files.forEach((f) => URL.revokeObjectURL(f.preview));
    setFiles([]);
  }, [files]);

  return (
    <div className="glass-card space-y-4 p-6" data-onboarding="photo-upload">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Upload Photos</h3>
          <p className="text-xs text-muted-foreground">
            Upload property photos. AI will classify rooms and detect issues automatically.
          </p>
        </div>
        {files.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={clsx(
          'relative flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed transition-colors',
          isDragging
            ? 'border-primary bg-primary/10'
            : 'border-border/60 bg-white/30 hover:border-primary/40 hover:bg-white/50 dark:bg-white/5 dark:hover:bg-white/10'
        )}
      >
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFiles(e.target.files)}
          className="absolute inset-0 cursor-pointer opacity-0"
        />
        <CloudArrowUpIcon className="h-12 w-12 text-muted-foreground" />
        <p className="mt-2 text-sm font-medium text-foreground">
          Drop photos here or click to browse
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Supports JPG, PNG, WEBP • Multiple files allowed
        </p>
      </div>

      {/* Preview Grid */}
      {files.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">
              {files.length} photo{files.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
            {files.map((file) => (
              <div
                key={file.id}
                className="group relative aspect-square overflow-hidden rounded-xl border border-white/20 bg-white/20"
              >
                <img
                  src={file.preview}
                  alt={file.file.name}
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeFile(file.id)}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                  <p className="truncate text-xs text-white">{file.file.name}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="space-y-2 rounded-xl border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-primary">{uploadProgress.current}</span>
                <span className="text-muted-foreground">
                  {uploadProgress.completed}/{uploadProgress.total}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-primary/20">
                <div
                  className="h-2 rounded-full bg-primary transition-all"
                  style={{ width: `${(uploadProgress.completed / uploadProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => uploadMutation.mutate()}
              disabled={uploadMutation.isPending || files.length === 0}
              className="btn-glass btn-primary focus-ring inline-flex items-center gap-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            >
              <PhotoIcon className="h-4 w-4" />
              {uploadMutation.isPending
                ? 'Processing...'
                : `Upload ${files.length} photo${files.length !== 1 ? 's' : ''} & Analyze`}
            </button>
          </div>

          {uploadMutation.isError && (
            <p className="text-sm text-[hsl(var(--destructive))]">
              Upload failed. Please try again.
            </p>
          )}
        </div>
      )}

      {/* Empty state info */}
      {files.length === 0 && (
        <div className="rounded-xl border border-border/40 bg-white/20 p-4 text-sm dark:bg-white/5">
          <p className="font-medium text-foreground">How it works:</p>
          <ol className="mt-2 list-inside list-decimal space-y-1 text-muted-foreground">
            <li>Upload all property photos at once</li>
            <li>AI classifies each photo into rooms (Kitchen, Bathroom, etc.)</li>
            <li>AI detects issues and conditions in each photo</li>
            <li>Review organized results and generate your report</li>
          </ol>
        </div>
      )}
    </div>
  );
}
